import { Typography } from '@mui/material';
import React from 'react';
import {
  BooleanInput,
  Edit,
  FormTab,
  NumberInput,
  required,
  SelectInput,
  TabbedForm,
  TextInput,
} from 'react-admin';

import {
  ClientCalendarPlatform,
  ClientVideoPlatform,
  Prefix,
} from '../../../graphql/types';
import { EnvLevel, getEnvLevel } from '../../../utils/getEnvLevel';
import { ResourceLink } from '../../reusable/ResourceLink';
import EditToolbar from '../components/EditToolbar';
import ImageUploadInput from '../components/ImageUploadInput';
import * as Labels from './labels';

const DisplayNameInput: React.FC = () => {
  const charLimit = 20;
  return (
    <TextInput
      source="displayName"
      label="Display Name"
      helperText="This will be seen by app users (max 20 char)"
      inputProps={{ maxLength: charLimit }}
      style={{ marginBottom: 26 }}
      validate={required()}
    />
  );
};

export const ClientEdit: React.FC = () => {
  const envLevel = getEnvLevel();
  return (
    <Edit mutationMode="pessimistic" redirect={false}>
      <TabbedForm toolbar={<EditToolbar />}>
        <FormTab label="Basics">
          <TextInput source="id" InputProps={{ disabled: true }} />
          <TextInput source="name" label="Client Name" />
          <DisplayNameInput />
          <ImageUploadInput
            source="logo"
            prefix={Prefix.Uploads}
            label="Client Logo"
            helperText={false}
          />

          <Typography variant="subtitle2">
            {Labels.SUBTITLE_INTEGRATIONS}
          </Typography>
          <SelectInput
            label="Calendar Platform"
            source="calendarPlatform"
            id="calendarPlatform"
            choices={Object.entries(ClientCalendarPlatform).map(
              ([name, id]) => ({
                id,
                name,
              }),
            )}
            validate={required()}
            helperText={false}
          />
          <SelectInput
            label="Video Platform"
            source="videoPlatform"
            id="videoPlatform"
            choices={Object.entries(ClientVideoPlatform).map(([name, id]) => ({
              id,
              name,
            }))}
            validate={required()}
            helperText={false}
          />

          <Typography variant="subtitle2">
            {Labels.SUBTITLE_OPERATIONS}
          </Typography>
          <BooleanInput
            source="isArchived"
            label={Labels.IS_ARCHIVED}
            helperText={false}
          />
          <BooleanInput
            source="isFcmOnly"
            label={Labels.IS_FCM_ONLY}
            helperText={Labels.IS_FCM_ONLY_HELPER_TEXT}
          />
          <BooleanInput
            source="usesSSO"
            label={Labels.USES_SSO}
            helperText={Labels.USES_SSO_HELPER_TEXT}
            disabled={true}
          />
        </FormTab>
        <FormTab label="Configs" gap={2} data-testid="tab-Fit">
          <Typography variant="subtitle2">
            {Labels.SUBTITLE_ELIGIBILITY}
          </Typography>
          <BooleanInput
            source="requiresEligibleEmailForGym"
            label={Labels.REQUIRES_ELIGIBLE_EMAIL_FOR_GYM}
            helperText="If ON, members must have an fcm-eligible email to access the gym. Only for clients using MBO SSO. "
          />
          <BooleanInput
            source="usesFitnessCenterForEligibility"
            label={Labels.USES_FITNESS_CENTER_FOR_ELIGIBILITY}
            helperText="If ON, members must have an active membership in MBO to access the Exos app."
          />
          <BooleanInput
            source="usesEligibilityServiceType"
            label={Labels.USES_ELIGIBILITY_SERVICE_TYPE}
            helperText="If ON, member must also have a special service package in MBO to access the Exos app."
          />

          <Typography variant="subtitle2">
            {Labels.SUBTITLE_OPT_IN_FEATURES}
          </Typography>
          <BooleanInput
            source="usesPersonalTraining"
            label={Labels.USES_PERSONAL_TRAINING}
            helperText={false}
          />
          <BooleanInput
            source="usesHighPerformanceCoaching"
            label={Labels.USES_HIGH_PERFORMANCE_COACHING}
            helperText={
              envLevel === EnvLevel.PROD ? (
                <>
                  Use{' '}
                  <ResourceLink name="EligibleEmailAddress" type="list">
                    Eligible Email Addresses
                  </ResourceLink>{' '}
                  to control access to this feature.
                </>
              ) : (
                false
              )
            }
            disabled={envLevel === EnvLevel.PROD}
          />
          <BooleanInput
            source="usesMboRegistrationFlow"
            label={Labels.USES_MBO_REGISTRATION_FLOW}
            helperText={false}
          />
          <BooleanInput
            source="usesMboSso"
            label={Labels.USES_MBO_SSO}
            helperText={`Turn on for clients that use MBO SSO so we don't send password reset emails. Does nothing if ${Labels.USES_MBO_REGISTRATION_FLOW} is off.`}
          />
          <BooleanInput
            source="usesCustomChallenges"
            label={Labels.USES_CUSTOM_CHALLENGES}
            helperText={false}
          />

          <Typography variant="subtitle2">
            {Labels.SUBTITLE_CLIENT_OFFERINGS}
          </Typography>
          <NumberInput
            source="maxFamilyMembers"
            label={Labels.MAX_FAMILY_MEMBERS}
            helperText="Set to 0 to disable family sharing for this client."
            min={0}
            max={99}
          />
          <BooleanInput
            source="collectsMemberData"
            label={Labels.COLLECTS_MEMBER_DATA}
            helperText="If ON, new users will be prompted to share data to get incentives."
          />
          <BooleanInput
            source="collectsMemberDataForEEP"
            label={Labels.COLLECTS_MEMBER_DATA_FOR_EEP}
            helperText="If ON, new users will be prompted to share data to improve the wellness program."
          />

          <Typography variant="subtitle2">
            {Labels.SUBTITLE_OTHER_CONFIGS}
          </Typography>
          <NumberInput
            source="inactivityDuration"
            label={Labels.INACTIVITY_DURATION}
            helperText="Set to 0 to disable inactivity timeout. If non-zero, user will be automatically logged out after that many minutes of inactivity."
            min={0}
            defaultValue={0}
          />
          <BooleanInput
            source="allowsCoachesToCreateMembers"
            label={Labels.ALLOWS_COACHES_TO_CREATE_MEMBERS}
            helperText={false}
          />
          <BooleanInput
            source="allowsInAppTransactions"
            label={Labels.ALLOWS_IN_APP_TRANSACTIONS}
            helperText={false}
          />
          <BooleanInput
            source="strictAppCheck"
            label={Labels.STRICT_APP_CHECK}
            helperText="Leave OFF unless specifically instructed by Engineering and Legal."
          />
          <BooleanInput
            source="needsUsToFixTheirUserNames"
            label={Labels.NEEDS_US_TO_FIX_THEIR_USERNAMES}
            helperText="Leave OFF unless specifically instructed by Engineering."
          />
        </FormTab>
      </TabbedForm>
    </Edit>
  );
};
